import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Chip,
  Divider,
  Stack,
  Typography,
  Paper
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  AccountTree as AccountTreeIcon,
  ArrowDownward as ArrowDownwardIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { STORE_VARIANT_CONFIG } from './WorkflowGuideStore';

/**
 * Beginner-friendly workflow diagrams for Operations Board, Crew Duty Roster,
 * Sales Enquiry, Aircraft Life-cycle Management, Maintenance & Inspections (per tab),
 * Technical Log Book, MRO Workbench, and PROCUREMENT AND STORE modules.
 * Collapsed by default so it does not clutter day-to-day work.
 */

const OPS_STEPS = [
  {
    title: 'Sales creates the charter',
    who: 'Sales',
    detail: 'Create a Trip (charter) and add Sector(s) — each sector is one flight leg.',
    result: 'Trip: DRAFT · Sector: PLANNED',
    where: '/sales'
  },
  {
    title: 'Send to Operations',
    who: 'Sales',
    detail: 'Hand the charter to Ops so it appears on this board.',
    result: 'Trip: PENDING_OPS',
    where: '/sales → /ops'
  },
  {
    title: 'Assign aircraft & pilots',
    who: 'Operations',
    detail:
      'Pick aircraft and pilots on the Operations Board. Duty roster / FDTL is managed separately on Crew Duty.',
    result: 'Sector: ASSIGNED',
    where: 'Operations Board'
  },
  {
    title: 'Pilots accept or reject',
    who: 'Crew',
    detail: 'Each assigned pilot responds in Crew Inbox. If anyone rejects, Ops must reassign.',
    result: 'All accept → CREW_ACCEPTED · Reject → CREW_REJECTED',
    where: '/crew'
  },
  {
    title: 'Enter flight requirements',
    who: 'Operations (any Ops role)',
    detail: 'Fuel, altitude, route, weather, and TripKit. Ops Manager reviews and verifies TripKit.',
    result: 'Still CREW_ACCEPTED until approvals finish',
    where: 'Operations Board'
  },
  {
    title: 'AM then CAMO approve',
    who: 'Accountable Manager → CAMO',
    detail: 'AM reviews requirements; then CAMO reviews airworthiness. Both must approve.',
    result: 'Sector: READY_FOR_BA',
    where: 'Approvals'
  },
  {
    title: 'Breath Analyzer (BA) video',
    who: 'Crew (or Ops)',
    detail: 'Upload BA test video. Failed tests can be retried.',
    result: 'READY_FOR_BA → READY_FOR_ATC',
    where: '/crew or Ops'
  },
  {
    title: 'ATC clearance',
    who: 'Ops → Crew',
    detail: 'Ops requests and sends clearance; crew signs it.',
    result: 'READY_FOR_ATC → CLEARED → COMPLETED',
    where: 'Ops + /crew'
  },
  {
    title: 'Technical log',
    who: 'Crew',
    detail: 'Pilot submits the Technical log. When every sector is done, the charter closes.',
    result: 'SECTOR_COMPLETED → CHARTER_COMPLETED',
    where: '/crew'
  }
];

const OPS_STATUS_STRIP = [
  'PLANNED',
  'ASSIGNED',
  'CREW_ACCEPTED',
  'READY_FOR_BA',
  'READY_FOR_ATC',
  'CLEARED',
  'COMPLETED',
  'SECTOR_COMPLETED'
];

const SALES_STEPS = [
  {
    title: 'Create the charter (trip)',
    who: 'Sales',
    detail:
      'Enter charter title, client details, optional baggage weight, remarks, and sales template. A charter number is generated automatically (e.g. CH-GEA-0006).',
    result: 'Trip status: DRAFT',
    where: 'Flight Entry'
  },
  {
    title: 'Add sector(s)',
    who: 'Sales',
    detail:
      'Add one or more flight legs: sequence, departure/arrival airports, and dates/times (shown in the client display timezone). You need at least one sector before sending to Ops.',
    result: 'Each sector: PLANNED',
    where: 'Flight Entry'
  },
  {
    title: 'Add passengers (optional now)',
    who: 'Sales',
    detail:
      'Manage passengers and baggage weight per sector. Total sector weight later includes passengers + baggage + crew.',
    result: 'Passenger list saved on the charter/sectors',
    where: 'Manage Passengers'
  },
  {
    title: 'Send charter to Operations',
    who: 'Sales',
    detail:
      'Hand the draft charter to Ops. Sales must include at least one sector. After this, Ops owns day-to-day flight processing on the Operations Board.',
    result: 'Trip: DRAFT → PENDING_OPS',
    where: 'Flight Entry → /ops'
  },
  {
    title: 'Ops assigns aircraft & pilots',
    who: 'Operations',
    detail:
      'Ops picks aircraft and pilots on the Operations Board (no FDTL gate). Manage duty/rest separately on Crew Duty. Track progress here via Workflow Status.',
    result: 'Sector: ASSIGNED',
    where: 'Operations Board'
  },
  {
    title: 'Crew accept → requirements → AM/CAMO',
    who: 'Crew → Ops → AM → CAMO',
    detail:
      'Pilots accept, Ops enters flight requirements, Accountable Manager then CAMO approve. Approvals can also be actioned from Flight Entry for AM/CAMO roles.',
    result: 'Sector: READY_FOR_BA',
    where: '/crew · /ops · /sales'
  },
  {
    title: 'BA → ATC → Technical log → complete',
    who: 'Crew + Ops',
    detail:
      'Breath Analyzer, ATC clearance, and Technical log finish each sector. When all sectors are done, the charter closes.',
    result: 'SECTOR_COMPLETED → CHARTER_COMPLETED',
    where: '/crew · /ops'
  }
];

const SALES_TRIP_STATUS_STRIP = [
  'DRAFT',
  'PENDING_OPS',
  'CREW_ASSIGNMENT',
  'READY_FOR_BA',
  'READY_FOR_ATC',
  'COMPLETED',
  'CHARTER_COMPLETED'
];

const ROSTER_FLOWS = [
  {
    title: 'A. Plan who can work',
    steps: [
      { label: 'Create duty or roster plan', who: 'Ops' },
      { label: 'Compliance check (FDTL / rest)', who: 'System' },
      { label: 'SCHEDULED (or override / block)', who: 'Ops' }
    ]
  },
  {
    title: 'B. Leave & availability',
    steps: [
      { label: 'Crew requests leave / marks availability', who: 'Crew' },
      { label: 'Ops approves leave or overrides', who: 'Ops' },
      { label: 'Calendar shows available / unavailable', who: 'System' }
    ]
  },
  {
    title: 'C. Charter flights (separate)',
    steps: [
      { label: 'Ops assigns pilots on Operations Board', who: 'Ops' },
      { label: 'No auto duty create / FDTL gate', who: '—' },
      { label: 'Record flight duties on this roster if needed', who: 'Ops' }
    ]
  },
  {
    title: 'D. Publish roster',
    steps: [
      { label: 'Ops publishes (≥ 7 days ahead)', who: 'Ops' },
      { label: 'PDF generated · crew notified', who: 'System' }
    ]
  }
];

const StepCard = ({ index, title, who, detail, result, where }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.5,
      bgcolor: 'background.paper',
      borderColor: 'divider'
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box
        sx={{
          minWidth: 28,
          height: 28,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.85rem',
          fontWeight: 700,
          mt: 0.25
        }}
      >
        {index}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            {title}
          </Typography>
          <Chip label={who} size="small" color="primary" variant="outlined" />
          {where ? (
            <Chip label={where} size="small" variant="outlined" sx={{ color: 'text.secondary' }} />
          ) : null}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {detail}
        </Typography>
        <Typography variant="caption" color="text.primary">
          <Box component="span" fontWeight={600}>
            Result:{' '}
          </Box>
          {result}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

const FlowArrow = ({ horizontal = false }) =>
  horizontal ? (
    <ArrowForwardIcon sx={{ color: 'text.disabled', alignSelf: 'center', mx: 0.5, flexShrink: 0 }} fontSize="small" />
  ) : (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.5 }}>
      <ArrowDownwardIcon sx={{ color: 'text.disabled' }} fontSize="small" />
    </Box>
  );

const MiniFlow = ({ title, steps }) => (
  <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
      {title}
    </Typography>
    <Stack spacing={0.5}>
      {steps.map((step, i) => (
        <React.Fragment key={`${title}-${i}`}>
          <Box
            sx={{
              px: 1.25,
              py: 1,
              borderRadius: 1,
              bgcolor: 'action.hover',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              {step.label}
            </Typography>
            <Typography variant="caption" color="primary.main">
              {step.who}
            </Typography>
          </Box>
          {i < steps.length - 1 ? <FlowArrow /> : null}
        </React.Fragment>
      ))}
    </Stack>
  </Paper>
);

const StatusStrip = ({ statuses }) => (
  <Box
    sx={{
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 0.5,
      p: 1.5,
      borderRadius: 1,
      bgcolor: 'action.hover',
      border: '1px solid',
      borderColor: 'divider'
    }}
  >
    {statuses.map((status, i) => (
      <React.Fragment key={status}>
        <Chip label={status} size="small" color={i === statuses.length - 1 ? 'success' : 'default'} />
        {i < statuses.length - 1 ? (
          <ArrowForwardIcon sx={{ color: 'text.disabled', fontSize: 16 }} />
        ) : null}
      </React.Fragment>
    ))}
  </Box>
);

const OperationsWorkflowBody = () => (
  <Stack spacing={2}>
    <Typography variant="body2" color="text.secondary">
      Think of a charter as a trip with one or more flight legs (sectors). Sales creates it; this
      board runs it through assign → accept → requirements → approvals → BA → ATC → Technical log.
    </Typography>

    <Box>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        Sector status (happy path)
      </Typography>
      <StatusStrip statuses={OPS_STATUS_STRIP} />
    </Box>

    <Divider />

    <Typography variant="subtitle2" fontWeight={700}>
      Step-by-step
    </Typography>
    <Stack spacing={0}>
      {OPS_STEPS.map((step, i) => (
        <React.Fragment key={step.title}>
          <StepCard index={i + 1} {...step} />
          {i < OPS_STEPS.length - 1 ? <FlowArrow /> : null}
        </React.Fragment>
      ))}
    </Stack>

    <Divider />

    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
      Common side paths
    </Typography>
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
      <Chip label="Crew rejects → reassign" size="small" />
      <Chip label="BA / ATC failed → retry that step" size="small" />
      <Chip label="Schedule change before ATC → may reset to PLANNED" size="small" />
      <Chip label="Cancel sector/trip → CANCELLED" size="small" />
    </Stack>
  </Stack>
);

const RosterWorkflowBody = () => (
  <Stack spacing={2}>
    <Typography variant="body2" color="text.secondary">
      This page is the pilot duty calendar and compliance book (DGCA FDTL). Plan who can work,
      track leave, and create/update duty entries here. It is not linked to Operations Board
      charter assignment — Ops manages flights separately.
    </Typography>

    <Box>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        Standalone from Operations Board
      </Typography>
      <StatusStrip
        statuses={[
          'Create duty entry',
          'FDTL / leave check',
          'Duty SCHEDULED',
          'Mark complete',
          'Auto rest (if any)'
        ]}
      />
    </Box>

    <Divider />

    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: 1.5
      }}
    >
      {ROSTER_FLOWS.map((flow) => (
        <MiniFlow key={flow.title} title={flow.title} steps={flow.steps} />
      ))}
    </Box>

    <Divider />

    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
      Duty types & statuses (quick reference)
    </Typography>
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
      {['FLIGHT', 'TRAINING', 'STANDBY', 'RESERVE', 'OTHER', 'SCHEDULED_DUTY_OFF'].map((t) => (
        <Chip key={t} label={t} size="small" variant="outlined" />
      ))}
    </Stack>
    <StatusStrip statuses={['SCHEDULED', 'IN_PROGRESS', 'COMPLETED']} />
    <Typography variant="caption" color="text.secondary">
      SCHEDULED_DUTY_OFF and approved leave block assignment. Plan-only FLIGHT rows (Rostering tab)
      do not block charter assign. CANCELLED means the duty is no longer active.
    </Typography>
  </Stack>
);

const SalesWorkflowBody = () => (
  <Stack spacing={2}>
    <Typography variant="body2" color="text.secondary">
      Flight Entry is where new charters start. You create the trip and sectors here, then send
      them to Operations. After handoff you can still watch status, add remarks, manage passengers,
      generate itinerary (after crew accept), and cancel when allowed.
    </Typography>

    <Box>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        Charter (trip) status (happy path)
      </Typography>
      <StatusStrip statuses={SALES_TRIP_STATUS_STRIP} />
    </Box>

    <Divider />

    <Typography variant="subtitle2" fontWeight={700}>
      Step-by-step
    </Typography>
    <Stack spacing={0}>
      {SALES_STEPS.map((step, i) => (
        <React.Fragment key={step.title}>
          <StepCard index={i + 1} {...step} />
          {i < SALES_STEPS.length - 1 ? <FlowArrow /> : null}
        </React.Fragment>
      ))}
    </Stack>

    <Divider />

    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
      What Sales can still do after sending to Ops
    </Typography>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: 1.5
      }}
    >
      <MiniFlow
        title="Track & document"
        steps={[
          { label: 'Watch Workflow Status / audit trail', who: 'Sales' },
          { label: 'Add remarks anytime', who: 'Sales' },
          { label: 'PDF itinerary after crew acceptance', who: 'Sales' }
        ]}
      />
      <MiniFlow
        title="Change & cancel rules"
        steps={[
          { label: 'Add sectors until charter completed', who: 'Sales / Ops' },
          { label: 'Edit sector schedule before ATC cleared', who: 'Sales (may reset to PLANNED)' },
          { label: 'Cancel sector or whole trip when allowed', who: 'Sales / AM' }
        ]}
      />
    </Box>

    <Divider />

    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
      Common side paths
    </Typography>
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
      <Chip label="Cannot send to Ops without ≥1 sector" size="small" />
      <Chip label="AM or CAMO reject → fix and re-approve" size="small" />
      <Chip label="Crew reject → Ops reassigns" size="small" />
      <Chip label="Cancel → CANCELLED" size="small" />
    </Stack>
  </Stack>
);

const AIRCRAFT_LIFECYCLE_STEPS = [
  {
    title: 'Register the aircraft first',
    who: 'CAMO / Admin',
    detail:
      'Aircraft must exist (Aircraft page). Creating an aircraft auto-creates the Airframe assembly. ATA chapter on the aircraft is needed before other assemblies.',
    result: 'Aircraft exists · Airframe assembly created',
    where: '/aircraft'
  },
  {
    title: 'Select aircraft on this page',
    who: 'You',
    detail:
      'Open Dashboard for fleet summary, then pick an aircraft. Use Aircraft Usage for live hours/cycles from the latest approved Technical Log Book.',
    result: 'Aircraft selected · tabs unlock',
    where: 'Dashboard / Aircraft Usage'
  },
  {
    title: 'Create or edit an assembly',
    who: 'CAMO / MRO',
    detail:
      'Add major assemblies (Engine, Landing Gear, etc.) under the aircraft. Capture install date, position, work order, Time Since Records (TSN/TSO/…), and documents.',
    result: 'Assembly status: DRAFT',
    where: 'Assemblies tab'
  },
  {
    title: 'Submit assembly → approve or reject',
    who: 'Tech → CA Manager / AM',
    detail:
      'Executive roles submit for approval. Manager roles (CA Manager, Accountable Manager, Super Admin) approve or reject.',
    result: 'DRAFT → SUBMITTED → APPROVED (or REJECTED)',
    where: 'Assemblies tab'
  },
  {
    title: 'Add components on assemblies',
    who: 'CAMO / MRO',
    detail:
      'Fit parts on an assembly. Link Part Master from the Parts Catalog. Optionally mark Original Equipment to copy install details from the assembly. Track TSR limits.',
    result: 'Component status: DRAFT',
    where: 'Components tab'
  },
  {
    title: 'Submit component → approve or reject',
    who: 'Tech → CA Manager / AM',
    detail: 'Same draft → submit → approve pattern as assemblies.',
    result: 'DRAFT → SUBMITTED → APPROVED (or REJECTED)',
    where: 'Components tab'
  },
  {
    title: 'Services & inspections on components',
    who: 'CAMO / MRO',
    detail:
      'Record component services and inspections. They also go DRAFT → SUBMITTED → APPROVED → COMPLETED.',
    result: 'Service/Inspection completed',
    where: 'Component form'
  },
  {
    title: 'Track identity & history',
    who: 'System + you',
    detail:
      'Serialized assemblies/components get a Serialized Item identity. Full install/remove history lives under Serialized Item Lifecycle. Soft-archive old records when needed.',
    result: 'Audit trail + serial history kept',
    where: '/serialized-items'
  }
];

const AircraftLifecycleWorkflowBody = () => (
  <Stack spacing={2}>
    <Typography variant="body2" color="text.secondary">
      This page is the continuing-airworthiness record book for what is fitted on each aircraft.
      Hierarchy is simple: Aircraft → Assembly (e.g. Engine) → Component (individual part). Usage
      hours come from the Technical Log Book; due tracking uses Time Since Records.
    </Typography>

    <Box>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        Hierarchy (how the data nests)
      </Typography>
      <StatusStrip
        statuses={['Aircraft', 'Assembly', 'Component', 'Service / Inspection']}
      />
    </Box>

    <Box>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        Approval status (assemblies & components)
      </Typography>
      <StatusStrip statuses={['DRAFT', 'SUBMITTED', 'APPROVED']} />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
        REJECTED returns the record for correction. Soft-archive (show archived checkbox) hides old
        records without deleting history.
      </Typography>
    </Box>

    <Divider />

    <Typography variant="subtitle2" fontWeight={700}>
      Step-by-step
    </Typography>
    <Stack spacing={0}>
      {AIRCRAFT_LIFECYCLE_STEPS.map((step, i) => (
        <React.Fragment key={step.title}>
          <StepCard index={i + 1} {...step} />
          {i < AIRCRAFT_LIFECYCLE_STEPS.length - 1 ? <FlowArrow /> : null}
        </React.Fragment>
      ))}
    </Stack>

    <Divider />

    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
      What each tab is for
    </Typography>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: 1.5
      }}
    >
      <MiniFlow
        title="Dashboard & Aircraft Usage"
        steps={[
          { label: 'Dashboard — fleet overview', who: 'All roles' },
          { label: 'Select an aircraft', who: 'You' },
          { label: 'Aircraft Usage — live TLB totals', who: 'Read-only' }
        ]}
      />
      <MiniFlow
        title="Assemblies & Components"
        steps={[
          { label: 'Assemblies — major units on the aircraft', who: 'CAMO / MRO' },
          { label: 'Components — parts on those assemblies', who: 'CAMO / MRO' },
          { label: 'Export CSV / Show archived', who: 'These two tabs only' }
        ]}
      />
    </Box>

    <Divider />

    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
      Related pages
    </Typography>
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
      <Chip label="Aircraft (/aircraft) — register airframe" size="small" />
      <Chip label="Parts Catalog (/parts-catalog) — Part Master" size="small" />
      <Chip label="Serialized Item Lifecycle (/serialized-items)" size="small" />
      <Chip label="Maintenance — work orders / AMP / upcoming" size="small" />
    </Stack>

    <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ mt: 1 }}>
      Common tips
    </Typography>
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
      <Chip label="Managers approve; executives draft & submit" size="small" />
      <Chip label="TSR = TSN / TSO / TSI / TSP life tracking" size="small" />
      <Chip label="Install/remove moves driven by work orders" size="small" />
      <Chip label="Store roles: view + Parts Catalog" size="small" />
    </Stack>
  </Stack>
);

const MAINTENANCE_WO_STATUS_STRIP = [
  'SCHEDULED',
  'CAMO QM ack',
  'Planning',
  'Store / MRO',
  'IN_PROGRESS',
  'Dual completion',
  'COMPLETED'
];

const MAINTENANCE_WO_STEPS = [
  {
    title: 'Create / schedule the work order',
    who: 'CAMO',
    detail:
      'Create a WO for an aircraft (manual, from intimation, AMP, or AD/SB). If not linked to an intimation, Creation source / reason is required.',
    result: 'Status: SCHEDULED',
    where: 'All Work Orders · Schedule Maintenance'
  },
  {
    title: 'CAMO QM acknowledges creation',
    who: 'CAMO QM Manager',
    detail: 'Quality gate: acknowledge that the WO was created correctly before Planning proceeds.',
    result: 'Still SCHEDULED · camoQmAcknowledged = true',
    where: 'WO detail'
  },
  {
    title: 'Planning reviews and sets actions',
    who: 'Planning',
    detail:
      'Set Plan Date and mark needs Store and/or MRO action (at least one required). Add parts requirements when Store is needed.',
    result: 'Planning approved · ready for Store/MRO',
    where: 'WO detail'
  },
  {
    title: 'Store and/or MRO act',
    who: 'Store · MRO',
    detail:
      'Store accepts or rejects (may set parts availability date → Planning reschedules). MRO accepts to start work. Store may raise a separate parts-procurement WO.',
    result: 'MRO accept → IN_PROGRESS',
    where: 'WO detail · Store / MRO actions'
  },
  {
    title: 'MRO Engineer completes work',
    who: 'MRO Engineer',
    detail: 'Record action taken / completion. Work then waits for quality completion acknowledgments.',
    result: 'MRO_ENGINEER_COMPLETED',
    where: 'MRO Board / WO detail'
  },
  {
    title: 'Dual completion acknowledgments',
    who: 'MRO QM → CAMO QM',
    detail:
      'MRO QM acknowledges completion, then CAMO QM acknowledges (or rejects back to engineer). Both are required.',
    result: 'Status: COMPLETED',
    where: 'WO detail'
  },
  {
    title: 'Maintenance release certificate',
    who: 'Authorized staff',
    detail: 'Issue the release certificate when the WO is completed. Completed WOs also appear under Work Order History.',
    result: 'WO closed with certificate',
    where: 'WO detail · History tab'
  }
];

const MaintenanceDashboardBody = () => (
  <Stack spacing={2}>
    <Typography variant="body2" color="text.secondary">
      Dashboard is the at-a-glance view of maintenance workload: open work orders, queues waiting on
      CAMO QM / Planning / Store / MRO, and links into the other tabs to act.
    </Typography>
    <StatusStrip
      statuses={['See queues', 'Open the right tab', 'Act on WO / intimation / EDR']}
    />
    <Divider />
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: 1.5
      }}
    >
      <MiniFlow
        title="Typical next places"
        steps={[
          { label: 'All Work Orders — run the main WO pipeline', who: 'Most roles' },
          { label: 'Upcoming — what is due soon', who: 'Planning / CAMO' },
          { label: 'Intimations — request a WO to be created', who: 'Planning → CAMO' }
        ]}
      />
      <MiniFlow
        title="Special inboxes"
        steps={[
          { label: 'StandAlone WO — emergency ground aircraft', who: 'CA Manager / Tech' },
          { label: 'EDR — defects sent from MRO to CAMO', who: 'CAMO / Planning Manager' },
          { label: 'History — finished WOs + certificates', who: 'All roles' }
        ]}
      />
    </Box>
  </Stack>
);

const MaintenanceAllWorkOrdersBody = () => (
  <Stack spacing={2}>
    <Typography variant="body2" color="text.secondary">
      This tab is the main work-order pipeline for Maintenance & Inspections. Every normal WO moves
      through quality, planning, store/MRO gates, then dual completion.
    </Typography>
    <Box>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        Happy-path status strip
      </Typography>
      <StatusStrip statuses={MAINTENANCE_WO_STATUS_STRIP} />
    </Box>
    <Divider />
    <Typography variant="subtitle2" fontWeight={700}>
      Step-by-step
    </Typography>
    <Stack spacing={0}>
      {MAINTENANCE_WO_STEPS.map((step, i) => (
        <React.Fragment key={step.title}>
          <StepCard index={i + 1} {...step} />
          {i < MAINTENANCE_WO_STEPS.length - 1 ? <FlowArrow /> : null}
        </React.Fragment>
      ))}
    </Stack>
    <Divider />
    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
      Side paths
    </Typography>
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
      <Chip label="Store reject → fix parts / availability date" size="small" />
      <Chip label="MRO reject → Planning revisits" size="small" />
      <Chip label="CAMO QM reject completion → back to engineer" size="small" />
      <Chip label="AM can override / cancel when needed" size="small" />
      <Chip label="Parts procurement = separate linked WO" size="small" />
    </Stack>
  </Stack>
);

/** Lightweight inline notice (keeps guide self-contained). */
const GuideNotice = ({ children }) => (
  <Box
    sx={{
      p: 1.5,
      borderRadius: 1,
      bgcolor: 'action.hover',
      border: '1px solid',
      borderColor: 'divider'
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {children}
    </Typography>
  </Box>
);

const MaintenanceStandaloneBody = () => (
  <Stack spacing={2}>
    <Typography variant="body2" color="text.secondary">
      StandAlone WO is an emergency work order. Use it when the aircraft must be grounded for urgent
      work outside the normal schedule. Future charters can be cancelled — inform Sales and Operations.
    </Typography>
    <GuideNotice>
      Emergency stand-alone WOs are restricted to CA Manager / CA Technical Officer / Planning Manager
      (and AM / Super Admin). CAMO QM must review and acknowledge creation like a normal work order
      before Planning can act.
    </GuideNotice>
    <StatusStrip
      statuses={[
        'Create emergency WO',
        'CAMO QM review',
        'Planning',
        'Inform Sales & Ops',
        'Complete & release'
      ]}
    />
    <Divider />
    <Stack spacing={0}>
      {[
        {
          title: 'Create emergency stand-alone WO',
          who: 'CA Manager / CA Tech',
          detail: 'Use Create emergency stand-alone WO. Aircraft is treated as needing urgent maintenance. Status is SCHEDULED.',
          result: 'WO created · awaiting CAMO QM',
          where: 'StandAlone WO tab'
        },
        {
          title: 'CAMO QM reviews creation',
          who: 'CAMO QM Manager',
          detail: 'Same quality gate as a normal work order: acknowledge or reject before Planning proceeds.',
          result: 'Still SCHEDULED · camoQmAcknowledged = true',
          where: 'WO detail · CAMO QM Review'
        },
        {
          title: 'Coordinate with Sales & Operations',
          who: 'You + Ops / Sales',
          detail: 'Future charters on this aircraft may be cancelled. Notify those teams immediately.',
          result: 'Operations aware · charters handled',
          where: 'Outside / Ops Board'
        },
        {
          title: 'Execute and close like a normal WO',
          who: 'Planning + MRO + QM',
          detail: 'Planning approval, then complete work, dual acknowledgments, and release certificate as for other work orders.',
          result: 'COMPLETED · appears in History',
          where: 'WO detail'
        }
      ].map((step, i, arr) => (
        <React.Fragment key={step.title}>
          <StepCard index={i + 1} {...step} />
          {i < arr.length - 1 ? <FlowArrow /> : null}
        </React.Fragment>
      ))}
    </Stack>
  </Stack>
);

const MaintenanceUpcomingBody = () => (
  <Stack spacing={2}>
    <Typography variant="body2" color="text.secondary">
      Upcoming Maintenance shows what is due soon from AMP schedules, AD/SB, assembly/component life
      limits (TSR), and related sources. You do not create the WO here directly — you create an
      intimation (request) for CAMO to turn into a work order.
    </Typography>
    <StatusStrip
      statuses={[
        'Set due range (Days/Hours/Cycles/Landings)',
        'Review due rows',
        'Create intimation',
        'CAMO creates / links WO'
      ]}
    />
    <Divider />
    <Stack spacing={0}>
      {[
        {
          title: 'Choose the due window',
          who: 'Planning / CAMO',
          detail:
            'Use Days, Hours, Cycles, and/or Landings range controls. The active range chip shows which filter drives the list.',
          result: 'Filtered upcoming list',
          where: 'Upcoming tab filters'
        },
        {
          title: 'Review each due item',
          who: 'You',
          detail:
            'Rows may show remaining/due lines for AMP, live component TSR remaining, and whether an intimation already exists.',
          result: 'Know what needs action',
          where: 'Upcoming table'
        },
        {
          title: 'Create intimation (if none yet)',
          who: 'Planning',
          detail:
            'Create Intimation from the row. Source details are taken from the upcoming item. If an intimation ID is shown, open it on the Intimations tab.',
          result: 'Intimation PENDING',
          where: '→ Intimations tab'
        }
      ].map((step, i, arr) => (
        <React.Fragment key={step.title}>
          <StepCard index={i + 1} {...step} />
          {i < arr.length - 1 ? <FlowArrow /> : null}
        </React.Fragment>
      ))}
    </Stack>
    <Divider />
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
      <Chip label="Also configure schedules on AMP Schedules / AD-SB pages" size="small" />
      <Chip label="One intimation per upcoming row when created from here" size="small" />
    </Stack>
  </Stack>
);

const MaintenanceIntimationsBody = () => (
  <Stack spacing={2}>
    <Typography variant="body2" color="text.secondary">
      Intimations are requests to create (or link) a work order. Planning raises them; CAMO (CA
      Manager / Technical Officer) responds. They are not the WO itself — the WO is created or linked
      when CAMO acknowledges.
    </Typography>
    <StatusStrip
      statuses={['PENDING', 'CLARIFICATION_REQUESTED', 'ACKNOWLEDGED', 'WO linked']}
    />
    <Divider />
    <Stack spacing={0}>
      {[
        {
          title: 'Create intimation',
          who: 'Planning',
          detail:
            'Ad-hoc from this tab (with source text) or prefilled from Upcoming Maintenance. Edit allowed until acknowledged.',
          result: 'Status: PENDING',
          where: 'Intimations tab'
        },
        {
          title: 'Respond to Intimation',
          who: 'CA Manager / CA Tech',
          detail:
            'Use the highlighted Respond to Intimation button on a PENDING row. Create a new WO, link an existing WO by number, or request clarification from Planning.',
          result: 'ACKNOWLEDGED (+ WO) or CLARIFICATION_REQUESTED',
          where: 'Respond to Intimation button'
        },
        {
          title: 'Planning replies to clarification',
          who: 'Planning',
          detail: 'Answer the clarification. Status returns to PENDING for CAMO to act again.',
          result: 'PENDING again',
          where: 'Intimation detail'
        },
        {
          title: 'Archive when done with the request',
          who: 'Planning / CAMO',
          detail:
            'Soft-archive; restore if needed. Permanent delete only when archived and no WO references it.',
          result: 'ARCHIVED (or deleted)',
          where: 'Intimations tab'
        }
      ].map((step, i, arr) => (
        <React.Fragment key={step.title}>
          <StepCard index={i + 1} {...step} />
          {i < arr.length - 1 ? <FlowArrow /> : null}
        </React.Fragment>
      ))}
    </Stack>
  </Stack>
);

const MaintenanceHistoryBody = () => (
  <Stack spacing={2}>
    <Typography variant="body2" color="text.secondary">
      Work Order History lists finished work orders (completed pipeline, certificates, and closed
      outcomes). Use it for audit, CSV export, and looking up past maintenance — not for active gates.
    </Typography>
    <StatusStrip
      statuses={['WO completed', 'Release certificate', 'Find in History', 'Export CSV']}
    />
    <Divider />
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: 1.5
      }}
    >
      <MiniFlow
        title="When a WO lands here"
        steps={[
          { label: 'Dual completion finished', who: 'MRO QM + CAMO QM' },
          { label: 'Status COMPLETED', who: 'System' },
          { label: 'Optional release certificate on file', who: 'Authorized staff' }
        ]}
      />
      <MiniFlow
        title="What you do on this tab"
        steps={[
          { label: 'Search / filter past WOs', who: 'Any role with access' },
          { label: 'Open detail for evidence', who: 'Audit / CAMO / MRO' },
          { label: 'Download CSV of history', who: 'You' }
        ]}
      />
    </Box>
  </Stack>
);

const MaintenanceEdrBody = () => (
  <Stack spacing={2}>
    <Typography variant="body2" color="text.secondary">
      EDR (Engineering Defect Report) is the CAMO inbox for defect forms MRO sends from a parent work
      order. From here CAMO can raise a child work order (parent number + letter suffix, e.g. WO-0001a).
    </Typography>
    <StatusStrip
      statuses={[
        'MRO fills EDR on parent WO',
        'System assigns EDR reference',
        'Send to CAMO',
        'Appears in EDR inbox',
        'Raise child EDR WO'
      ]}
    />
    <Divider />
    <Stack spacing={0}>
      {[
        {
          title: 'MRO captures the defect form',
          who: 'MRO Engineer',
          detail: 'On the parent WO (usually from MRO Board), save draft then Send to CAMO. The EDR reference number is assigned automatically (registration / EDR / sequence / year).',
          result: 'EDR marked sent',
          where: '/mro · parent WO'
        },
        {
          title: 'CAMO reviews inbox',
          who: 'CAMO roles / Planning Manager',
          detail: 'Open EDR tab, View the form (read-only), check aircraft / defect / authorisation number details.',
          result: 'Understand the defect',
          where: 'EDR tab'
        },
        {
          title: 'Raise EDR child work order',
          who: 'CAMO',
          detail:
            'Create EDR WO linked to the parent. Number is parent WO + a/b/c… (max 26 per parent). Child then follows the normal WO pipeline.',
          result: 'Child WO SCHEDULED · isEdrWorkOrder',
          where: 'EDR tab → All Work Orders'
        }
      ].map((step, i, arr) => (
        <React.Fragment key={step.title}>
          <StepCard index={i + 1} {...step} />
          {i < arr.length - 1 ? <FlowArrow /> : null}
        </React.Fragment>
      ))}
    </Stack>
    <Divider />
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
      <Chip label="Visible to all CAMO roles, Planning Manager, AM, Super Admin" size="small" />
      <Chip label="Deep link from CAMO Team Dashboard" size="small" />
    </Stack>
  </Stack>
);

const TLB_STATUS_STRIP = [
  'DRAFT',
  'SUBMITTED',
  'UNDER_REVIEW',
  'APPROVED'
];

const TLB_STEPS = [
  {
    title: 'Optional: open TLB early for a charter',
    who: 'Ops / MRO Engineer',
    detail:
      'On Operations Board, assign an MRO engineer and open a DRAFT Technical Log Book for the trip (CHARTER_PREOPEN). Pilots or the engineer can fill sector slots after each leg.',
    result: 'Open DRAFT sheet linked to the charter',
    where: '/ops · Open TLB'
  },
  {
    title: 'Pilot uploads Flight Log (POST_FLIGHT)',
    who: 'Crew',
    detail:
      'After the flight, pilot submits Flight Log Entry from Crew Inbox. System either merges into the open charter TLB or auto-creates a new TLB entry with pilot-filled fields (FLIGHT_LOG_AUTO).',
    result: 'TLB exists in DRAFT with pilot data',
    where: '/crew · Enter Techlog'
  },
  {
    title: 'CA Technical Officer completes CAMO fields',
    who: 'CA Technical Officer',
    detail:
      'Fill remaining sheet fields: maintenance/AME data, defects, totals brought/carried forward, sheet number, and other CAMO sections. Up to 5 sectors can share one sheet.',
    result: 'DRAFT ready to submit',
    where: 'Technical Log Book entry'
  },
  {
    title: 'Submit for review',
    who: 'CA Technical Officer',
    detail: 'Submit locks editing for the officer. Sheet number is required before submit.',
    result: 'DRAFT → SUBMITTED',
    where: 'Entry actions'
  },
  {
    title: 'CA Manager reviews',
    who: 'CA Manager',
    detail: 'Manager may mark UNDER_REVIEW, then approve or reject with remarks.',
    result: 'UNDER_REVIEW → APPROVED or REJECTED',
    where: 'Entry actions'
  },
  {
    title: 'Approved becomes the usage source of truth',
    who: 'System',
    detail:
      'Latest APPROVED TLB with totals feeds Aircraft Usage / live hours on Aircraft Life-cycle. Rejected returns to DRAFT for correction and resubmit.',
    result: 'APPROVED · live usage updated',
    where: 'Aircraft Usage · this list'
  },
  {
    title: 'Correction after approval (if needed)',
    who: 'CAMO',
    detail:
      'Request correction on an APPROVED entry (remarks required). Status goes CORRECTION_REQUESTED then unlocks as DRAFT. Must re-submit and re-approve.',
    result: 'DRAFT again → full review cycle',
    where: 'Entry · Correct'
  }
];

const TechnicalLogBookWorkflowBody = () => (
  <Stack spacing={2}>
    <Typography variant="body2" color="text.secondary">
      Technical Log Book is the digital Flight Sector Report that matches the physical book on the
      aircraft. Pilots start the data (flight log); CAMO completes the sheet; CA Manager approves.
      One sheet can hold up to five sectors.
    </Typography>

    <Box>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        Status (happy path)
      </Typography>
      <StatusStrip statuses={TLB_STATUS_STRIP} />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
        REJECTED unlocks editing and returns to DRAFT. APPROVED entries can be unlocked only via
        Correction (then re-submit + re-approve).
      </Typography>
    </Box>

    <Divider />

    <Typography variant="subtitle2" fontWeight={700}>
      Step-by-step
    </Typography>
    <Stack spacing={0}>
      {TLB_STEPS.map((step, i) => (
        <React.Fragment key={step.title}>
          <StepCard index={i + 1} {...step} />
          {i < TLB_STEPS.length - 1 ? <FlowArrow /> : null}
        </React.Fragment>
      ))}
    </Stack>

    <Divider />

    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
      How an entry can be created
    </Typography>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
        gap: 1.5
      }}
    >
      <MiniFlow
        title="Charter pre-open"
        steps={[
          { label: 'Ops / MRO opens DRAFT for trip', who: 'Ops Board' },
          { label: 'Fill sector slots during / after flight', who: 'Crew / MRO' },
          { label: 'POST_FLIGHT merges into same sheet', who: 'System' }
        ]}
      />
      <MiniFlow
        title="Auto from flight log"
        steps={[
          { label: 'Pilot uploads POST_FLIGHT', who: 'Crew' },
          { label: 'System creates TLB (FLIGHT_LOG_AUTO)', who: 'System' },
          { label: 'CAMO completes & submits', who: 'CA Tech' }
        ]}
      />
      <MiniFlow
        title="Standalone / Create entry"
        steps={[
          { label: 'Create entry (backfill) when allowed', who: 'CAMO / Admin' },
          { label: 'Fill sheet manually', who: 'CA Tech' },
          { label: 'Submit → Manager approve', who: 'CAMO' }
        ]}
      />
    </Box>

    <Divider />

    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
      Who does what
    </Typography>
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
      <Chip label="Crew — flight log / sector slots" size="small" />
      <Chip label="MRO Engineer — open & fill charter TLB" size="small" />
      <Chip label="CA Technical Officer — complete & submit" size="small" />
      <Chip label="CA Manager — review / approve / reject" size="small" />
      <Chip label="Ops — view · assign MRO · open TLB" size="small" />
    </Stack>

    <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ mt: 1 }}>
      Related
    </Typography>
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
      <Chip label="Physical copies: White=CAMO · Yellow=Ops · Pink=Aircraft" size="small" />
      <Chip label="Format pinned per entry (TLB Format admin)" size="small" />
      <Chip label="Approved totals → Aircraft Usage live hours" size="small" />
    </Stack>
  </Stack>
);

const MRO_WORKBENCH_STEPS = [
  {
    title: 'Work order reaches MRO',
    who: 'After Planning',
    detail:
      'On Maintenance, Planning marks needs MRO action (and Store if needed). Once planning is approved and store gates are clear, the WO appears on this workbench.',
    result: 'Visible in Pending work / MRO pipeline',
    where: '/maintenance → /mro'
  },
  {
    title: 'MRO Manager accepts (or rejects)',
    who: 'MRO Manager',
    detail:
      'Open Pending work → MRO manager. Accept to take the job; reject sends it back for Planning to revisit.',
    result: 'Accepted → work can proceed (often IN_PROGRESS)',
    where: 'Pending work · Manager'
  },
  {
    title: 'MRO Engineer executes the work',
    who: 'MRO Engineer',
    detail:
      'Expand the WO: review issued parts from Store, update components (add/remove/replace), fill TLB totals when relevant, raise an EDR if a defect must go to CAMO.',
    result: 'Work documented on the WO',
    where: 'Pending work · Engineer · Pipeline'
  },
  {
    title: 'Engineer marks step complete',
    who: 'MRO Engineer',
    detail: 'Submit MRO engineer completion (or reject the step back if work is not ready).',
    result: 'mroEngineerCompleted',
    where: 'Engineer actions'
  },
  {
    title: 'MRO QM reviews completion',
    who: 'MRO QM',
    detail:
      'Pending work → MRO QM: approve completion, reject, or send back to engineer. Full WO review is available on this tab.',
    result: 'mroQmCompletionAcknowledged',
    where: 'Pending work · MRO QM'
  },
  {
    title: 'CAMO QM closes the dual-completion gate',
    who: 'CAMO QM (on Maintenance)',
    detail:
      'Final quality acknowledgment happens on Maintenance & Inspections (not only here). Then the WO can be marked COMPLETED and a release certificate issued.',
    result: 'COMPLETED · certificate when ready',
    where: '/maintenance'
  },
  {
    title: 'Charter techlogs (parallel track)',
    who: 'Assigned MRO Engineer / Ops',
    detail:
      'Charter techlogs tab lists trips where you are the assigned MRO engineer. Open or fill the pre-opened Technical Log Book for that charter.',
    result: 'DRAFT TLB filled alongside flight work',
    where: 'Charter techlogs · /technical-log-book'
  }
];

const MroWorkbenchWorkflowBody = () => (
  <Stack spacing={2}>
    <Typography variant="body2" color="text.secondary">
      MRO Workbench is where Maintenance, Repair &amp; Overhaul staff accept work orders, perform the
      work, raise EDRs, and complete quality steps. Pending work is action-first; the pipeline is the
      full list; Charter techlogs covers TLB for assigned flights.
    </Typography>

    <Box>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        MRO slice of the maintenance pipeline
      </Typography>
      <StatusStrip
        statuses={[
          'Planning done',
          'MRO accept',
          'Engineer works',
          'Engineer complete',
          'MRO QM ack',
          'CAMO QM ack',
          'COMPLETED'
        ]}
      />
    </Box>

    <Divider />

    <Typography variant="subtitle2" fontWeight={700}>
      Step-by-step
    </Typography>
    <Stack spacing={0}>
      {MRO_WORKBENCH_STEPS.map((step, i) => (
        <React.Fragment key={step.title}>
          <StepCard index={i + 1} {...step} />
          {i < MRO_WORKBENCH_STEPS.length - 1 ? <FlowArrow /> : null}
        </React.Fragment>
      ))}
    </Stack>

    <Divider />

    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
      What each main tab is for
    </Typography>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
        gap: 1.5
      }}
    >
      <MiniFlow
        title="Pending work"
        steps={[
          { label: 'MRO manager — accept / reject', who: 'Manager' },
          { label: 'MRO engineer — do the job', who: 'Engineer' },
          { label: 'MRO QM — completion review', who: 'QM' }
        ]}
      />
      <MiniFlow
        title="Work orders (MRO pipeline)"
        steps={[
          { label: 'Browse all WOs in the MRO lane', who: 'Manager / oversight' },
          { label: 'Engineers see scoped rows', who: 'Engineer' },
          { label: 'Expand row — issued parts & actions', who: 'You' }
        ]}
      />
      <MiniFlow
        title="Charter techlogs"
        steps={[
          { label: 'See charters assigned to you', who: 'MRO Engineer' },
          { label: 'Open / fill DRAFT TLB', who: 'You' },
          { label: 'Continues on Technical Log Book', who: 'CAMO later' }
        ]}
      />
    </Box>

    <Divider />

    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
      Common actions on a work order
    </Typography>
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
      <Chip label="View issued parts from Store" size="small" />
      <Chip label="Add / remove / replace components" size="small" />
      <Chip label="EDR → Send to CAMO inbox" size="small" />
      <Chip label="Update TLB totals when linked" size="small" />
      <Chip label="Second page / PDF as available" size="small" />
    </Stack>
  </Stack>
);

const MAINTENANCE_TAB_CONFIG = {
  dashboard: {
    title: 'Dashboard — how this tab works',
    Body: MaintenanceDashboardBody
  },
  all: {
    title: 'All Work Orders — workflow diagram',
    Body: MaintenanceAllWorkOrdersBody
  },
  standalone: {
    title: 'StandAlone WO — emergency workflow',
    Body: MaintenanceStandaloneBody
  },
  upcoming: {
    title: 'Upcoming Maintenance — workflow diagram',
    Body: MaintenanceUpcomingBody
  },
  intimations: {
    title: 'Intimations to Create WO — workflow diagram',
    Body: MaintenanceIntimationsBody
  },
  history: {
    title: 'Work Order History — how this tab works',
    Body: MaintenanceHistoryBody
  },
  edr: {
    title: 'EDR inbox — workflow diagram',
    Body: MaintenanceEdrBody
  }
};

const VARIANT_CONFIG = {
  operations: {
    title: 'How Operations Board works (workflow diagram)',
    Body: OperationsWorkflowBody
  },
  crewDutyRoster: {
    title: 'How Crew Duty works (workflow diagram)',
    Body: RosterWorkflowBody
  },
  sales: {
    title: 'How Flight Entry works (workflow diagram)',
    Body: SalesWorkflowBody
  },
  aircraftLifecycle: {
    title: 'How Aircraft Life-cycle Management works (workflow diagram)',
    Body: AircraftLifecycleWorkflowBody
  },
  technicalLogBook: {
    title: 'How Technical Log Book works (workflow diagram)',
    Body: TechnicalLogBookWorkflowBody
  },
  mroWorkbench: {
    title: 'How MRO Workbench works (workflow diagram)',
    Body: MroWorkbenchWorkflowBody
  },
  ...STORE_VARIANT_CONFIG
};

/**
 * @param {'operations' | 'crewDutyRoster' | 'sales' | 'aircraftLifecycle' | 'maintenance' | 'technicalLogBook' | 'mroWorkbench' | 'storeActivities' | 'storeInventory' | 'storeReturns' | 'receivingAndInspection' | 'issuance' | 'procurement' | 'loan'} variant
 * @param {string} [tab] - For variant="maintenance": dashboard | all | standalone | upcoming | intimations | history | edr
 * @param {boolean} [defaultExpanded]
 */
const WorkflowGuide = ({ variant, tab, defaultExpanded = false }) => {
  let config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.operations;
  if (variant === 'maintenance') {
    config = MAINTENANCE_TAB_CONFIG[tab] || MAINTENANCE_TAB_CONFIG.all;
  }
  const Body = config.Body;

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      sx={{
        mb: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        '&:before': { display: 'none' },
        overflow: 'hidden'
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          bgcolor: 'action.hover',
          '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 }
        }}
      >
        <AccountTreeIcon color="primary" fontSize="small" />
        <Typography variant="subtitle1" fontWeight={700}>
          {config.title}
        </Typography>
        <Chip label="Beginner guide" size="small" color="info" variant="outlined" sx={{ ml: 1 }} />
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 2 }}>
        <Body />
      </AccordionDetails>
    </Accordion>
  );
};

export default WorkflowGuide;
